import dagre from '@dagrejs/dagre';
import type { Node, Edge } from '@xyflow/react';
import type { LayoutConfig } from '@/types/react-flow';

// Node dimensions must match the actual rendered WorkflowCard component
// WorkflowCard is w-72 = 288px, min-h-32 = 128px
// Must stay in sync with asl-converter.ts NODE_WIDTH/NODE_HEIGHT
const DEFAULT_NODE_WIDTH = 288;
const DEFAULT_NODE_HEIGHT = 128;

export type { LayoutConfig };

/**
 * Apply dagre layout to nodes and edges.
 * 
 * NOTE: Child nodes (those with parentId) are excluded from the dagre layout
 * and retain their original positions. This is necessary for nested node
 * containers like Map states where child nodes have positions relative to
 * their parent container.
 * 
 * IMPORTANT: React Flow requires parent nodes to appear BEFORE their children
 * in the nodes array. This function preserves the original ordering while
 * only updating positions for root nodes.
 * 
 * @see https://reactflow.dev/learn/layouting/sub-flows
 */
export function getLayoutedElements<N extends Record<string, unknown>, E extends Record<string, unknown>>(
    nodes: Node<N>[],
    edges: Edge<E>[],
    config: LayoutConfig
): { nodes: Node<N>[]; edges: Edge<E>[] } {
    const dagreGraph = new dagre.graphlib.Graph();

    dagreGraph.setGraph({
        rankdir: config.direction,
        align: config.align,
        ranksep: config.rankSeparation,
        nodesep: config.nodeSeparation,
        ranker: config.ranker ?? 'network-simplex',
    });

    dagreGraph.setDefaultEdgeLabel(() => ({}));

    // Identify child nodes (have parentId) - these keep their original positions
    const childNodeIds = new Set(
        nodes.filter((node) => !!node.parentId).map((n) => n.id)
    );

    // Add only root nodes (no parentId) to dagre graph
    nodes.forEach((node) => {
        if (!node.parentId) {
            // For container nodes (like mapContainer), use their explicit dimensions
            const width = node.style?.width as number ?? node.width ?? node.measured?.width ?? DEFAULT_NODE_WIDTH;
            const height = node.style?.height as number ?? node.height ?? node.measured?.height ?? DEFAULT_NODE_HEIGHT;
            dagreGraph.setNode(node.id, { width, height });
        }
    });

    // Add only edges between root nodes to dagre graph
    // (exclude edges where source or target is a child node)
    edges.forEach((edge) => {
        if (!childNodeIds.has(edge.source) && !childNodeIds.has(edge.target)) {
            dagreGraph.setEdge(edge.source, edge.target);
        }
    });

    // Run layout only if there are nodes in the graph
    const hasRootNodes = nodes.some((n) => !n.parentId);
    if (hasRootNodes) {
        dagre.layout(dagreGraph);
    }

    // Map positions back to nodes, preserving original array order
    // This is critical: React Flow requires parent nodes to appear BEFORE children
    const layoutedNodes = nodes.map((node) => {
        // Child nodes keep their original positions (relative to parent)
        if (node.parentId) {
            return node;
        }

        // Root nodes get their position from dagre
        const nodeWithPosition = dagreGraph.node(node.id);
        const width = node.style?.width as number ?? node.width ?? node.measured?.width ?? DEFAULT_NODE_WIDTH;
        const height = node.style?.height as number ?? node.height ?? node.measured?.height ?? DEFAULT_NODE_HEIGHT;

        return {
            ...node,
            position: {
                x: nodeWithPosition.x - width / 2,
                y: nodeWithPosition.y - height / 2,
            },
        };
    });

    return { nodes: layoutedNodes, edges };
}

/**
 * Filter nodes/edges to show only descendants of a root node
 */
export function filterByRootId<N extends Record<string, unknown>, E extends Record<string, unknown>>(
    nodes: Node<N>[],
    edges: Edge<E>[],
    rootId: string | undefined
): { nodes: Node<N>[]; edges: Edge<E>[] } {
    if (!rootId) {
        return { nodes, edges };
    }

    const visibleNodeIds = new Set<string>();
    const visibleEdges = new Set<Edge<E>>();

    // Recursive function to find all descendants
    const findDescendants = (nodeId: string) => {
        visibleNodeIds.add(nodeId);
        edges.forEach((edge) => {
            if (edge.source === nodeId) {
                visibleEdges.add(edge);
                findDescendants(edge.target);
            }
        });
    };

    findDescendants(rootId);

    return {
        nodes: nodes.map((node) => ({
            ...node,
            hidden: !visibleNodeIds.has(node.id),
        })),
        edges: edges.map((edge) => ({
            ...edge,
            hidden: !visibleEdges.has(edge),
        })),
    };
}

